import { ReactNode, useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { GlassBox } from 'components/glassBox/GlassBox';

import CircleArrowLeftIcon from 'assets/icons/daily-rewards/circle-arrow-left.svg';
import CloseIcon from 'assets/icons/daily-rewards/close-plain.svg';

const ANIMATION_DURATION = 200;

type DailyRewardsModalProps = {
  children: ReactNode;
  title?: string;
  onClose?: () => void;
  onPrevious?: () => void;
  closeButtonPosition?: 'top-left' | 'top-right';
  previousButtonPosition?: 'top-left' | 'top-right';
  className?: string;
  titleClassName?: string;
};

export const DailyRewardsModal = ({
  children,
  title,
  onClose,
  onPrevious,
  closeButtonPosition = 'top-right',
  previousButtonPosition = 'top-left',
  className,
  titleClassName,
}: DailyRewardsModalProps) => {
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    if (onClose) {
      setTimeout(onClose, ANIMATION_DURATION);
    }
  }, [onClose]);

  useEffect(() => {
    requestAnimationFrame(() => {
      setIsVisible(true);
    });
  }, []);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleClose]);

  return (
    <div className="tw-fixed tw-inset-0 tw-top-[54px] tw-z-[100] tw-flex tw-items-center tw-justify-center tw-overflow-y-auto tw-px-[10px] tw-py-[20px] tw-font-jost min-[576px]:tw-top-[62px] min-[576px]:tw-px-[26px] bp-md:tw-px-[44px] bp-2xl:tw-px-0 bp-3xl:tw-top-[70px] bp-4xl:tw-top-[86px]">
      <div
        className={twMerge(
          'tw-fixed tw-inset-0 tw-bg-black/20 tw-transition-opacity tw-duration-200',
          isVisible ? 'tw-opacity-100' : 'tw-opacity-0'
        )}
        style={{
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
        }}
        onClick={handleClose}
      />

      <GlassBox
        gradient="linear-gradient(to bottom right, #2D5EFF 0%, rgba(45, 94, 255, 0.2) 100%)"
        borderRadius="10px"
        backgroundOpacity={0.2}
        blurAmount="20px"
        className={twMerge(
          'tw-relative tw-max-h-full tw-w-full tw-transition-all tw-duration-200 bp-md:tw-max-w-[1200px]',
          isVisible ? 'tw-scale-100 tw-opacity-100' : 'tw-scale-95 tw-opacity-0',
          className
        )}
      >
        {title ? (
          <>
            <div className="tw-flex tw-items-center tw-justify-between tw-px-[15px] tw-pt-[15px]">
              <div className="tw-flex tw-h-[24px] tw-w-[24px] tw-shrink-0 tw-items-center tw-justify-center">
                {onPrevious && (
                  <img
                    src={CircleArrowLeftIcon}
                    alt="Previous"
                    onClick={onPrevious}
                    className="tw-block tw-h-[24px] tw-w-[24px] tw-cursor-pointer"
                  />
                )}
              </div>
              <h2
                className={twMerge(
                  'tw-m-0 tw-text-[32px] tw-font-bold tw-text-white lg:tw-text-[40px]',
                  titleClassName
                )}
              >
                {title}
              </h2>
              <div className="tw-flex tw-h-[24px] tw-w-[24px] tw-shrink-0 tw-items-center tw-justify-center">
                {onClose && (
                  <img
                    src={CloseIcon}
                    alt="Close"
                    onClick={handleClose}
                    className="tw-block tw-h-[24px] tw-w-[24px] tw-cursor-pointer"
                  />
                )}
              </div>
            </div>
            {children}
          </>
        ) : (
          <>
            {onPrevious && (
              <button
                onClick={onPrevious}
                className={twMerge(
                  'tw-absolute tw-z-10 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0',
                  previousButtonPosition === 'top-left'
                    ? 'tw-left-[15px] tw-top-[15px]'
                    : 'tw-right-[15px] tw-top-[15px]'
                )}
              >
                <img src={CircleArrowLeftIcon} alt="Previous" className="tw-h-[24px] tw-w-[24px]" />
              </button>
            )}

            {onClose && (
              <button
                onClick={handleClose}
                className={twMerge(
                  'tw-absolute tw-z-10 tw-cursor-pointer tw-border-none tw-bg-transparent tw-p-0',
                  closeButtonPosition === 'top-left' ? 'tw-left-[15px] tw-top-[15px]' : 'tw-right-[15px] tw-top-[15px]'
                )}
              >
                <img src={CloseIcon} alt="Close" className="tw-h-[24px] tw-w-[24px]" />
              </button>
            )}

            {children}
          </>
        )}
      </GlassBox>
    </div>
  );
};
